import {useCallback, useState} from 'react';
import * as Sentry from '@sentry/react';
import {S3UploadOptions, S3UploadResponse, S3UploadError} from './types';
import {s3Config} from '../../s3client';

type S3UploadResult = {
  data: S3UploadResponse | null;
  error: S3UploadError | null;
};

export const useS3Upload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<S3UploadError | null>(null);

  const uploadFile = useCallback(
    async (file: File, options: S3UploadOptions = {}): Promise<S3UploadResult> => {
      const {contentType = file.type, acl = 'private'} = options;

      setIsUploading(true);
      setProgress(0);
      setError(null);

      const fileExtension = file.name.split('.').pop();
      const uniqueFileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExtension}`;

      try {
        const [{S3Client}, {Upload}] = await Promise.all([
          import('@aws-sdk/client-s3'),
          import('@aws-sdk/lib-storage'),
        ]);
        const s3Client = new S3Client({
          region: s3Config.region,
          credentials: {
            accessKeyId: s3Config.accessKeyId,
            secretAccessKey: s3Config.secretAccessKey,
          },
        });
        const upload = new Upload({
          client: s3Client,
          params: {
            Bucket: s3Config.bucket,
            Key: `uploads/biomatch/liveness-detection/${uniqueFileName}`,
            Body: file,
            ContentType: contentType,
            ACL: acl,
          },
        });

        upload.on('httpUploadProgress', progressEvent => {
          const percentUploaded = Math.round(
            ((progressEvent.loaded || 0) / (progressEvent.total || 1)) * 100,
          );
          setProgress(percentUploaded);
        });

        await upload.done();
        const url = `https://${s3Config.bucket}.s3.${s3Config.region}.amazonaws.com/uploads/biomatch/liveness-detection/${uniqueFileName}`;

        const response: S3UploadResponse = {
          url,
          key: uniqueFileName,
          bucket: s3Config.bucket,
        };

        return {
          data: response,
          error: null,
        };
      } catch (err) {
        Sentry.captureException(err);
        const s3Error: S3UploadError = {
          code: (err as Error).name || 'UnknownError',
          message: (err as Error).message || 'An unknown error occurred',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          requestId: (err as any).requestId,
        };

        setError(s3Error);

        return {
          data: null,
          error: s3Error,
        };
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  return {
    uploadFile,
    isUploading,
    progress,
    error,
  };
};
